import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import PhotoCameraIcon from "@material-ui/icons/PhotoCamera";
import { makeStyles } from "@material-ui/core/styles";
import { authGet, authPut, AuthRequest } from "../../lib";

/**
 * Trade Step 1 — the short web form, a port of initial_request.php.
 *
 * Row colour is the SCORE, not the status: a red-to-green scale, the way the
 * legacy sheet coloured these. Scoring itself is commented out in the live
 * template, so the score is shown but not edited here — the endpoint exists if
 * it is ever switched back on.
 */

const STATUSES = ["Quoted", "Pending", "Completed", "Spam", "No"];

/** color-option-1..5 in the legacy stylesheet, worst to best. */
const SCORE_TINT = {
  "0-20": "#fd8c8c",
  "20-40": "#f8e3e3",
  "40-60": "#e0f8e2",
  "60-80": "#a8ffb0",
  "80-100": "#77ff7e",
};

const scoreStyles = Object.entries(SCORE_TINT).reduce((acc, [band, colour]) => {
  acc[`score${band.replace("-", "_")}`] = {
    backgroundColor: colour,
    // The pinned cells inherit this, so they scroll over the row opaquely.
    "& td": { backgroundColor: colour },
    "&:hover": { filter: "brightness(0.96)" },
  };
  return acc;
}, {});

const useStyles = makeStyles((theme) => ({
  /* The shell lays this out with align-items:center, which shrink-wraps a
     child to its content — so a wide table made the page itself wide, and the
     shell's overflowX:hidden then CLIPPED the last columns instead of
     scrolling them. Stretching and capping at 100% keeps the width owned by
     the viewport. */
  page: {
    alignSelf: "stretch",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    margin: "24px 0 0",
  },
  panel: { padding: theme.spacing(2.5), maxWidth: "100%" },
  scroller: { overflowX: "auto", maxWidth: "100%" },
  /* Sticky cells do nothing under border-collapse: collapse, which is MUI's
     default for a Table. */
  table: { borderCollapse: "separate", borderSpacing: 0 },
  tableWide: { minWidth: 1500 },
  sticky: {
    position: "sticky",
    right: 0,
    background: "inherit",
    boxShadow: "-6px 0 6px -6px rgba(0,0,0,0.25)",
  },
  stickyHead: {
    position: "sticky",
    right: 0,
    background: theme.palette.background.paper,
    zIndex: 2,
  },
  stickyLink: { position: "sticky", right: 120, background: "inherit" },
  stickyLinkHead: {
    position: "sticky",
    right: 120,
    background: theme.palette.background.paper,
    zIndex: 2,
  },
  head: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  title: { fontSize: 18, fontWeight: 500, flex: "none" },
  spacer: { flex: 1 },
  search: {
    width: 260,
    "& .MuiOutlinedInput-root": { height: 31 },
    "& .MuiOutlinedInput-input": { padding: "0 4px", fontSize: 13 },
    "& .MuiInputAdornment-root": { marginRight: 2 },
  },
  searchIcon: { fontSize: 17, color: theme.palette.text.secondary },
  count: { color: theme.palette.text.secondary, fontSize: 13 },
  exportRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  date: { width: 150 },
  row: { height: 38, backgroundColor: theme.palette.background.paper },
  cell: {
    fontSize: 12.5,
    padding: "4px 8px",
    borderBottom: "1px solid rgba(224,224,224,1)",
  },
  headCell: {
    fontSize: 11.5,
    fontWeight: 600,
    padding: "6px 8px",
    whiteSpace: "nowrap",
    borderBottom: "1px solid rgba(224,224,224,1)",
  },
  nowrap: { whiteSpace: "nowrap" },
  strong: { color: "rgba(0,0,0,0.87)" },
  muted: { color: theme.palette.text.secondary },
  wrap: { maxWidth: 200, wordWrap: "break-word", whiteSpace: "normal" },
  pick: { fontSize: 12.5, "&:before": { borderBottom: "1px solid rgba(0,0,0,0.15)" } },
  linkCell: { display: "flex", alignItems: "center", gap: 4 },
  linkInput: { width: 78, "& input": { fontSize: 12.5, padding: "2px 4px" } },
  linkBtn: { fontSize: 11, minWidth: 0, padding: "2px 6px" },
  /* a { color: inherit; text-decoration: none } is set globally, so a mailto
     has to say it is a link. */
  mailto: {
    color: theme.palette.primary.main,
    textDecoration: "underline",
    "&:hover": { filter: "brightness(0.85)" },
  },
  goTo: {
    color: theme.palette.primary.main,
    cursor: "pointer",
    fontSize: 12.5,
    "&:hover": { textDecoration: "underline" },
  },
  /* The customer's uploaded photos. Shown here because it is the one thing
     that decides whether a request is worth opening. */
  photo: {
    display: "inline-flex",
    alignItems: "center",
    gap: 2,
    fontSize: 11,
    fontWeight: 700,
    color: "#4a148c",
    background: "rgba(74,20,140,0.10)",
    borderRadius: 3,
    padding: "0 4px",
    marginLeft: 6,
    verticalAlign: "middle",
  },
  photoIcon: { fontSize: 12 },
  saved: { color: "#1b5e20", fontSize: 11, marginLeft: 6 },
  loading: { padding: theme.spacing(4), textAlign: "center" },
  error: { color: "#c62828", fontSize: 13 },
  ...scoreStyles,
}));

const when = (s) => {
  if (!s) return "";
  const d = new Date(String(s).replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return String(s);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  })}`;
};

const yesNo = (v) => (v === null || v === undefined || v === "" ? "-" : Number(v) ? "Yes" : "No");

/**
 * Columns, in the legacy order.
 *
 * The `optional` ones are hidden until "More columns" is pressed, picked on
 * how much they actually carry: Box and Papers are set on 426 of 4,278 rows,
 * Country is US on 97% of them, and Heard About Us is long free text that
 * forces the table wider than any screen. What remains is what you triage
 * on — who, which watch, trade or sell, and the two columns you act on.
 */
const COLUMNS = [
  { key: "request_date", label: "Request Date" },
  { key: "id", label: "ID" },
  { key: "name", label: "Full Name" },
  { key: "email", label: "Email" },
  { key: "country", label: "Country", optional: true },
  { key: "brand", label: "Brand" },
  { key: "make_model", label: "Make & Model" },
  { key: "warranty_date", label: "Warranty Date", optional: true },
  { key: "box", label: "Box", optional: true },
  { key: "papers", label: "Papers", optional: true },
  { key: "intent", label: "Trade or Sell" },
  { key: "heard", label: "Heard About Us From", optional: true },
  { key: "new_or_preowned", label: "New or Preowned", optional: true },
  { key: "referrer", label: "Source", optional: true },
  { key: "status", label: "Status", width: 120 },
  { key: "link", label: "Trade-In Step 2 ID", width: 150 },
];

export function TradeRequestsPage() {
  const classes = useStyles();
  const history = useHistory();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [range, setRange] = useState({ start: "", end: "" });
  const [wide, setWide] = useState(false);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(100);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState({});
  const [links, setLinks] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    const q = new URLSearchParams();
    if (status) q.set("status", status);
    if (query) q.set("search", query);
    if (range.start && range.end) {
      q.set("start", range.start);
      q.set("end", range.end);
    }
    const { data, error: err } = await authGet(`/trade-requests?${q.toString()}`);
    setLoading(false);

    if (err) {
      setError(err.message || "Could not load trade requests.");
      return;
    }
    setError("");
    setRows(data?.rows ?? []);
    setTotal(data?.total ?? 0);
    setLinks(
      Object.fromEntries(
        (data?.rows ?? []).map((r) => [r.initial_trade_in_id, r.trade_in_id ?? ""]),
      ),
    );
  }, [status, query, range]);

  useEffect(() => {
    load();
  }, [load]);

  // A new filter means a new result set; page 3 of the old one is meaningless.
  useEffect(() => {
    setPage(0);
  }, [status, query, range]);

  /*
   * Rendered a page at a time, as the legacy grid did at 100 rows. Every row
   * carries a live Select, so drawing all 500 at once puts hundreds of MUI
   * controls on the page and the screen crawls — which reads as a slow search
   * even though the query takes milliseconds.
   */
  const shown = rows.slice(page * perPage, page * perPage + perPage);
  const columns = COLUMNS.filter((c) => wide || !c.optional);

  const flash = (id) => {
    setSaved((s) => ({ ...s, [id]: true }));
    setTimeout(() => setSaved((s) => ({ ...s, [id]: false })), 1500);
  };

  const setRowStatus = (r) => async (e) => {
    const next = e.target.value;
    const id = r.initial_trade_in_id;
    setRows((list) =>
      list.map((x) => (x.initial_trade_in_id === id ? { ...x, status: next } : x)),
    );
    const { error: err } = await authPut(`/trade-requests/${id}/status`, { status: next });
    if (err) {
      setError(err.message || "Could not save that status.");
      load();
      return;
    }
    flash(id);
  };

  const saveLink = (r) => async () => {
    const id = r.initial_trade_in_id;
    setError("");
    const { data, error: err } = await authPut(`/trade-requests/${id}/link`, {
      trade_in_id: links[id] ?? "",
    });
    if (err) {
      setError(err.message || "Could not link that trade-in.");
      return;
    }
    setRows((list) =>
      list.map((x) =>
        x.initial_trade_in_id === id ? { ...x, trade_in_id: data.trade_in_id } : x,
      ),
    );
    flash(id);
  };

  /**
   * The export returns a file, so it needs the auth header — fetched as a blob
   * and handed to the browser, the same as the order reports.
   */
  const exportCsv = async () => {
    setBusy(true);
    setError("");
    try {
      const q = new URLSearchParams();
      if (range.start && range.end) {
        q.set("start", range.start);
        q.set("end", range.end);
      }
      const res = await AuthRequest.get(`/trade-requests/export?${q.toString()}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        range.start && range.end
          ? `trade-requests-${range.start}-to-${range.end}.csv`
          : "trade-requests.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      // The error body arrives as a blob too, so it has to be read back.
      let message = err?.message || "Could not build that export.";
      const body = err?.response?.data;
      if (body instanceof Blob) {
        try {
          message = JSON.parse(await body.text())?.message || message;
        } catch (_) {
          /* keep the generic message */
        }
      }
      setError(message);
    }
    setBusy(false);
  };

  const submit = (e) => {
    e?.preventDefault?.();
    setQuery(search.trim());
  };

  /** One body cell. Kept in one place so head and body cannot drift apart. */
  const renderCell = (col, r) => {
    const id = r.initial_trade_in_id;
    switch (col.key) {
      case "request_date":
        return <span className={classes.nowrap}>{when(r.request_date)}</span>;
      case "id":
        return <span className={classes.strong}>{id}</span>;
      case "name":
        return (
          <span className={classes.nowrap}>
            {[r.first_name, r.mi, r.last_name].filter(Boolean).join(" ")}
          </span>
        );
      case "email":
        return (
          <a className={classes.mailto} href={`mailto:${r.email}`} title={r.email}>
            {r.email}
          </a>
        );
      case "country":
        return r.country;
      case "brand":
        return r.brand_name || r.brand;
      case "make_model":
        return (
          <>
            {r.make_model}
            {!!r.photos?.length && (
              <span
                className={classes.photo}
                title={`${r.photos.length} photo${
                  r.photos.length > 1 ? "s" : ""
                } uploaded by the customer`}
              >
                <PhotoCameraIcon className={classes.photoIcon} />
                {r.photos.length}
              </span>
            )}
          </>
        );
      case "warranty_date":
        return r.warranty_date;
      case "box":
        return yesNo(r.has_box);
      case "papers":
        return yesNo(r.has_papers);
      case "intent":
        return r.intent;
      case "heard":
        return r.heard_about_us;
      case "new_or_preowned":
        return r.new_or_preowned === "preowned" ? (
          "Preowned"
        ) : r.new_or_preowned === "new" ? (
          "New"
        ) : (
          <span className={classes.muted}>—</span>
        );
      case "referrer":
        return r.referrer || <span className={classes.muted}>—</span>;
      case "status":
        return (
          <>
            <Select
              value={r.status || "Pending"}
              onChange={setRowStatus(r)}
              disableUnderline
              className={classes.pick}
              MenuProps={{
                getContentAnchorEl: null,
                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                transformOrigin: { vertical: "top", horizontal: "left" },
                MenuListProps: { dense: true, disablePadding: true },
              }}
            >
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
            {saved[id] && <span className={classes.saved}>Saved</span>}
          </>
        );
      case "link":
        return (
          <div className={classes.linkCell}>
            <TextField
              className={classes.linkInput}
              placeholder="Insert ID"
              value={links[id] ?? ""}
              onChange={(e) => setLinks((l) => ({ ...l, [id]: e.target.value }))}
              onKeyDown={(e) => e.key === "Enter" && saveLink(r)()}
            />
            <Button className={classes.linkBtn} size="small" onClick={saveLink(r)}>
              Link
            </Button>
            {r.trade_in_id ? (
              <span
                className={classes.goTo}
                title="Open this trade-in"
                onClick={() => history.push(`/trade-ins/${r.trade_in_id}`)}
              >
                →
              </span>
            ) : null}
          </div>
        );
      default:
        return null;
    }
  };

  /*
   * Columns are pinned only while the table is wider than the screen. With
   * the compact set there is nothing to scroll, and a pinned column would
   * just draw a shadow down the middle of the table.
   */
  const cellClass = (col) => {
    if (!wide) return classes.cell;
    if (col.key === "link") return `${classes.cell} ${classes.sticky}`;
    if (col.key === "status") return `${classes.cell} ${classes.stickyLink}`;
    if (col.key === "heard" || col.key === "referrer") return `${classes.cell} ${classes.wrap}`;
    return classes.cell;
  };

  const headClass = (col) => {
    if (!wide) return classes.headCell;
    if (col.key === "link") return `${classes.headCell} ${classes.stickyHead}`;
    if (col.key === "status") return `${classes.headCell} ${classes.stickyLinkHead}`;
    return classes.headCell;
  };

  return (
    <Box className={classes.page}>
      <Paper className={classes.panel}>
        <div className={classes.head}>
          <Typography className={classes.title}>Initial Trade Request</Typography>

          <ButtonGroup size="small">
            <Button
              onClick={() => setStatus("")}
              variant={status === "" ? "contained" : "outlined"}
              color={status === "" ? "primary" : "default"}
            >
              All
            </Button>
            {STATUSES.map((s) => (
              <Button
                key={s}
                onClick={() => setStatus(s)}
                variant={status === s ? "contained" : "outlined"}
                color={status === s ? "primary" : "default"}
              >
                {s}
              </Button>
            ))}
          </ButtonGroup>

          <form onSubmit={submit}>
            <TextField
              className={classes.search}
              placeholder="Name, email, brand or model"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onBlur={submit}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon className={classes.searchIcon} />
                  </InputAdornment>
                ),
              }}
            />
          </form>

          <Button
            size="small"
            variant="outlined"
            onClick={() => setWide((w) => !w)}
            title={
              wide
                ? "Hide the extra columns"
                : "Also show country, warranty date, box, papers, source and how they heard about us"
            }
          >
            {wide ? "Show Fewer Columns" : "Show More Columns"}
          </Button>

          <div className={classes.spacer} />
          <span className={classes.count}>
            {loading ? "Loading…" : `${total.toLocaleString()} shown`}
          </span>
        </div>

        <div className={classes.exportRow}>
          <TextField
            className={classes.date}
            label="From"
            type="date"
            value={range.start}
            onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
          />
          <TextField
            className={classes.date}
            label="To"
            type="date"
            value={range.end}
            onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
          />
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={busy}
            onClick={exportCsv}
          >
            {busy ? "Building…" : "Export to CSV"}
          </Button>
          {(range.start || range.end) && (
            <Button size="small" onClick={() => setRange({ start: "", end: "" })}>
              Clear dates
            </Button>
          )}
        </div>

        {error && <div className={classes.error}>{error}</div>}

        <div className={wide ? classes.scroller : undefined}>
          <Table size="small" className={`${classes.table} ${wide ? classes.tableWide : ""}`}>
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={col.key}
                    className={headClass(col)}
                    style={col.width ? { width: col.width } : undefined}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((r) => {
                const tint =
                  classes[`score${String(r.score_rating ?? "").replace("-", "_")}`] ?? "";
                return (
                  <TableRow key={r.initial_trade_in_id} className={`${classes.row} ${tint}`}>
                    {columns.map((col) => (
                      <TableCell key={col.key} className={cellClass(col)}>
                        {renderCell(col, r)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {!loading && rows.length > perPage && (
          <TablePagination
            component="div"
            count={rows.length}
            page={page}
            onChangePage={(_, p) => setPage(p)}
            rowsPerPage={perPage}
            onChangeRowsPerPage={(e) => {
              setPerPage(Number(e.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[50, 100, 250, 500]}
          />
        )}

        {loading && (
          <div className={classes.loading}>
            <CircularProgress size={22} />
          </div>
        )}
        {!loading && !rows.length && (
          <div className={classes.loading}>
            <span className={classes.muted}>No trade requests match.</span>
          </div>
        )}
      </Paper>
    </Box>
  );
}

export default TradeRequestsPage;
